<template>
  <div>
    <div v-show="portfolioType === 'modelportfolio'" >
        <div class="columns">
          <div class="column is-two-thirds">
            <PageHeading class="stk-mb-6">
              <template v-slot:title>
                Portfolios
              </template>
              <template v-slot:subtitle>
                Analyze, report, and edit portfolios. Upload via csv or manual entry.
              </template>
            </PageHeading>
          </div>
        </div>
      </div>
    <nav class="level stk-mb-6">
      <div class="level-left">
        <div class="control is-expanded has-icons-right">
          <input
            class="input is-primary is-medium"
            type="text"
            v-model="searchText"
            v-on:input="search"
            ref="input"
            placeholder="Search here"
          >
          <span class="icon is-right has-text-primary">
            <i class="fas fa-search"></i>
          </span>
        </div>
      </div>
      <div class="level-right">
        <div class="control " v-show="portfolioType === 'modelportfolio'">
          <Button
            href="/advisordashboard/model_portfolio_upload/"
            icon-after="fas fa-plus"
            variant="primary"
            size="5"
          >
            Create New Portfolio
          </Button>
        </div>
      </div>
    </nav>
    <div
        v-show="portfolioType === 'shareowner' "
      >
        <article class="message is-primary">
          <div class="message-body">
            <div class="columns">
              <div class="column is-narrow">
                <span class="icon has-text-primary"><i class="fas fa-circle"></i></span>
              </div>
              <div class="column has-text-dark">
                <p class="title is-5">
                  Enable Client Data Sync via Integration 
                </p>
                <p>
                  Sync client data from a custodian or platform to report on existing client portfolios.
                  You're welcome to contact support@yourstake.org for assistance. 
                  Your compliance department should approve data syncs. 
                </p>
              </div>
              <div class="column is-narrow">
                <br>
                <IntegrationsButton v-bind:advisor="$root.advisor"  />
              </div>
            </div><!-- columns -->
          </div><!-- meessage body -->
        </article>
        <div v-show="portfolios.length <= 1">
          <div class="notification is-primary is-light">
            You have not yet enabled client data sync. 
            <br>
            When integrations are active, client names and account holdings will appear here. 
          </div>
        </div>
      </div>

    <div>
      <PortfolioDetailCard
        v-for="portfolio in portfolios"
        v-bind:portfolio="portfolio"
        v-bind:modelPortfolios="[]"
        v-show="!searchText"
        :advisor="advisor"
      />

      <PortfolioDetailCard
        v-for="portfolio in searchResults"
        v-bind:portfolio="portfolio"
        v-bind:modelPortfolios="[]"
        v-show="searchText"
        :advisor="advisor"
      />

      <div v-show="ready && (searchText && searchResults.length === 0)">
        No matching portfolios.
      </div>

      <div class="block has-text-centered">
        <a ref="infiniteScrollAnchor" name="infiniteScrollAnchor" />
      </div>
    </div>
    <div v-show="!ready" class="has-text-centered">
      <b-loading v-bind:is-full-page="false" v-model="ready"/>
      <p>Loading portfolios...</p>
    </div>
  </div>
</template>

<script>
import eventBus from '@frontend/shared/EventBus.js';
import PortfolioDetailCard from '../PortfolioDetailCard.vue';
import IntegrationsButton from '@frontend/dash3/js/components/integrations/generic/IntegrationsButton.vue';
import { Button, PageHeading } from '@stakeui';

import _ from 'lodash';

export default {
  data() {
    return {
      portfolios: [],
      searchResults: [],
      searchText: '',
      ready: false,
      next: null,
      end: false,
      searchRequestId: 0,

      scrollObserver: null,
    };
  },
  props: {
    portfolioType: String,
    advisor: {
      type: Object,
      required: false,
    },
  },
  components: {
    PortfolioDetailCard, IntegrationsButton,
    Button,
    PageHeading,
  },
  methods: {
    
    loadPortfolios: async function() {
      let url;

      if (this.end) {
        return;
      }

      this.ready = false;

      if (this.next) {
        console.log('Loading next page...');
        url = this.next;
      } else {
        console.log('Loading portfolios...');
        url = this.portfolioType === 'prospect' ? `/api/v1/prospects/` : `/api/v1/portfolios/?type=${this.portfolioType}&ordering=-updated_at&light_serializer=true`;
      }

      const response = await fetch(url);
      const data = await response.json();
      if (data.portfolios) {
        this.portfolios.push(...data.portfolios);
      } else {
        this.portfolios.push(...data.prospects);
      }

      if (!data.next) { // Prevent future page loads when subsequent pages return with no next page
        this.end = true;
      } else {
        this.next = data.next;
        if (!this.scrollObserver) {
          this.scrollObserver = new IntersectionObserver(this.maybeLoadPortfolios);
          this.scrollObserver.observe(this.$refs.infiniteScrollAnchor);
        }
      }

      this.ready = true;
    },
    maybeLoadPortfolios: function(entries, observer) {
      for (const i in entries) {
        if (entries[i].isIntersecting) {
          console.log(`Loading more ${this.portfolioType} portfolios`);
          return this.loadPortfolios();
        }
      }
    },
    updatePortfolio: function(portfolio) {
      for (let i = 0; i < this.portfolios.length; i++) {
        if (this.portfolios[i].id === portfolio.id) {
          this.portfolios.splice(i, 1, JSON.parse(JSON.stringify(portfolio)));
        }
      }
    },
    search: function() {
      if (!this.searchText) {
        this.debouncedSearch.cancel();
        this.searchRequestId += 1;
        this.searchResults = [];
        this.ready = true;
        return;
      }

      this.ready = false;
      this.debouncedSearch();
    },
    doSearch: async function() {
      const query = this.searchText;
      if (!query) {
        this.searchResults = [];
        this.ready = true;
        return;
      }

      const requestId = ++this.searchRequestId;
      const queryParams = {
        search: query,
      };
      if (this.portfolioType !== 'prospect') {
        queryParams.type = this.portfolioType;
      }

      const url = this.portfolioType === 'prospect' ? `/api/v1/prospects/?${new URLSearchParams(queryParams)}` : `/api/v1/portfolios/?${new URLSearchParams(queryParams)}&light_serializer=true`;

      const response = await fetch(url);
      const data = await response.json();

      // Ignore stale responses from earlier queries
      if (requestId !== this.searchRequestId || query !== this.searchText) {
        return;
      }

      this.ready = true;
      this.searchResults = data.portfolios || data.prospects || [];
    },
  },
  created: async function() {
    this.loadPortfolios();

    this.debouncedSearch = _.debounce(this.doSearch, 400);
  },
  mounted: async function() {
    eventBus.$on('Portfolios:portfolio-updated', this.updatePortfolio);
  },

};
</script>

<style lang="css" scoped>
.message-body {
  background-color: white;
}
.message {
  box-shadow: rgba(149, 157, 165, 0.2) 0px 8px 24px;
}
</style>
